/**
 * Sentry privacy hooks (REF-6708 privacy slice B1): every telemetry copy of a URL, route or path goes
 * through the shared policy in `utils/privacy/telemetryUrl`, and console breadcrumbs are minimised.
 *
 * Wired IN PLACE by `instrument.ts`: `beforeSend` ends with `scrubEvent`, plus `beforeSendTransaction`,
 * `beforeSendSpan`, `beforeBreadcrumb`, Replay's `beforeAddRecordingEvent`, and — after `init`, through
 * `installSentryPrivacy` — a `createDsc` listener (the envelope HEADER `trace.transaction` is built from
 * the dynamic sampling context, which no item hook reaches) and a `replay_event` processor (`beforeSend`
 * does not run for replay events).
 *
 * `contexts.state` is skipped: Privacy A's Redux projection (`store/sentryRedux.ts`) already builds it
 * from an allowlist with no URL or free-text field.
 *
 * Known gap D-B1: rrweb's Meta / full-snapshot `href` inside the replay RECORDING is not a custom event,
 * so no hook reaches it. Pinned by `privacyHooks.replay.test.ts`.
 */
import * as Sentry from "@sentry/react";
import { PATHS } from "router/config/paths";
import { REDACTED, sanitizeDeep, sanitizeQuery, sanitizeRouteOrLabel, sanitizeUrl, sanitizeUrlsInText } from "utils/privacy/telemetryUrl";
import { scrubReplayRecordingEvent, scrubUploadSession } from "utils/sentry/scrubUploadSession";
import { SECRET_FIELD_NAMES } from "utils/linkToken";

type Options = Sentry.BrowserOptions;
type Event = Parameters<NonNullable<Options["beforeSend"]>>[0];
type TransactionEvent = Parameters<NonNullable<Options["beforeSendTransaction"]>>[0];
type Breadcrumb = Parameters<NonNullable<Options["beforeBreadcrumb"]>>[0];
type SpanJSON = Parameters<NonNullable<Options["beforeSendSpan"]>>[0];
type ReplayOptions = NonNullable<Parameters<typeof Sentry.replayIntegration>[0]>;
type RecordingEvent = Parameters<NonNullable<ReplayOptions["beforeAddRecordingEvent"]>>[0];
type ProcessedEvent = Parameters<Parameters<typeof Sentry.addEventProcessor>[0]>[0];
type Client = NonNullable<ReturnType<typeof Sentry.getClient>>;
type AnyRecord = Record<string, any>;

// ── console breadcrumbs ──────────────────────────────────────────────────────────────────────────
// User decision D1, option A (EXPLICIT VOCABULARY). Console breadcrumbs stay ON, but the SDK's raw copy —
// `data.arguments` (serialised at send and copied into Replay) and `message = args.map(String).join(" ")`
// — is replaced by a message built ONLY from:
//   - argument 0's leading bracket tag, kept only when it is in `CONSOLE_TAGS` (fixed, reviewed); any other
//     leading `[…]` of argument 0 becomes `<tag>`, and a bracket tag anywhere else is an unknown token;
//   - words from `CONSOLE_VOCABULARY` (below — fixed, reviewed, lower-cased on output) and the CANONICAL
//     credential keywords (`password`, `token`, `apikey`, … — a prefix such as `user_` / `user.` is dropped);
//   - primitives from an allowlist: booleans, null, undefined, integers 0–999 — but a RUN of two or more
//     numeric values (bare, or a `key=value` value; punctuation between them does not break the run)
//     becomes `<num>`, so a split phone number or date never survives digit by digit;
//   - a path only as a known `PATHS` route template (values → `:param`, query / fragment dropped), else
//     `<path>`; an origin only for a host in `CONSOLE_HOSTS`, else `<host>`;
//   - fixed markers: `[object]` `[array]` `[function]` `[symbol]` (by `typeof` / `Array.isArray` alone — no
//     property, getter, `Symbol.toStringTag`, prototype or method of an argument is touched), `[json]`,
//     `[text:<bucket>]` for a string over 1,000 chars (decided BEFORE any regex runs), `[redacted]`,
//     `[more]`, `…`, and `<tag>` / `<str>` / `<num>` / `<path>` / `<host>` for everything else.
// Credential adjacency FAILS CLOSED: after a credential keyword (bare, `key=` / `key:` with or without a
// value, any prefix), the REST of that argument and the WHOLE next argument that yields anything become one
// `[redacted]` — whatever they are, and also when only whitespace, a lone `=` / `:` or a format specifier
// sits in between (those are dropped, not consumed).
// Raw input is never trusted for looking like a marker: `[text:123…]` or `<num>` typed into a log is just
// an unknown token. The beforeSend second pass keeps only a message the first pass built and marked
// (`MINIMISED`, below); any other console breadcrumb without `arguments` is rebuilt from scratch.
// The vocabulary and the tag list grow by review only; an unknown word or tag costs readability, never privacy.
const CONSOLE_VOCABULARY = new Set(
  (
    "assertion failed failure error errors warning retry retrying reconnect reconnecting reconnected attempt attempts " +
    "timeout timed open opened close closed status start started stop stopped done success ok ready loading loaded " +
    "load save saved upload uploaded download request response connect connected disconnected socket websocket job " +
    "poll polling cancel cancelled abort aborted skip skipped invalid missing not found unknown expired network offline " +
    "online init render fetch for of to from in on at with no after before ms true false null undefined"
  ).split(" ")
);

// Every leading tag of a console call's first argument in src/ today (a TypeScript AST scan of `console.*`
// first arguments: string, template head or a same-file string const), plus the 7 static `prefix` values
// of `useMessageDeduplication`'s interpolated `[${prefix}]`. A new tag shows as `<tag>` until added here.
export const CONSOLE_TAGS: ReadonlySet<string> = new Set([
  "[AddStylingModal]",
  "[AdvancedTextAreaStatusListener]",
  "[Analytics]",
  "[Audio]",
  "[Brand Catalogue debug]",
  "[CORSLoader]",
  "[CanvasImageLimit]",
  "[CreateYourBrief]",
  "[DOM Patch]",
  "[EditDesignCanvasOverlay]",
  "[EditDesignCore]",
  "[EditTrainingModal]",
  "[GenerateModelTab]",
  "[HistoryFocus]",
  "[ImageCropper]",
  "[LassoCanvas]",
  "[LayoutIntentBatcher]",
  "[Memory]",
  "[ModelTrainingProvider]",
  "[ModelTrainingStatusListener]",
  "[NodeData]",
  "[SocketListener]",
  "[StartTrainingModal]",
  "[StyleTrainingStatusListener]",
  "[TechPack]",
  "[WebSocket]",
  "[WebSocketManager]",
  "[WriteLoop]",
  "[createTechpackInfo]",
  "[fabric_training]",
  "[handleAddPrint]",
  "[handleGenerate]",
  "[i18n]",
  "[magic_select_studio]",
  "[model_training]",
  "[prompt_assistant]",
  "[style_training]",
  "[tp_flat_conversion]",
  "[tp_sketch_conversion]",
  "[useBackgroundAssets]",
  "[useBackgroundTemplates]",
  "[useBackgroundUpload]",
  "[useFabricAssets]",
  "[useModelAssets]",
  "[useModelGeneration]",
  "[useModelTemplates]",
  "[usePoseAssets]",
  "[usePoseTemplates]",
  "[useSocketFallback]",
  "[useTimeoutApiCall]",
]);
// Any prefix (`reset_`, `x-refresh-`, `user.`) is accepted on input and never emitted: the output is the
// canonical base keyword, lower-cased, separators removed (`api_key` → `apikey`, `session-id` → `sessionid`).
const CREDENTIAL_KEYWORD =
  /(?:^|[._-])(password|passwd|pwd|passphrase|pass|secret|token|api[_-]?key|authorization|auth|bearer|basic|cookie|session(?:[_-]?id)?|signature|otp|pin|jwt|credentials?|refresh)$/i;
const canonicalKeyword = (word: string): string | null => {
  // `[` / `]` are prefix separators too, so a form-encoded key (`user[password]`) is recognised.
  const flat = word.replace(/[[\]]/g, ".").replace(/\.+$/, "");
  if (!/^[a-z0-9._-]+$/i.test(flat)) return null;
  const match = CREDENTIAL_KEYWORD.exec(flat);

  return match ? match[1].toLowerCase().replace(/[_-]/g, "") : null;
};
const CANONICAL_KEYWORDS = new Set(
  "password passwd pwd passphrase pass secret token apikey authorization auth bearer basic cookie session sessionid signature otp pin jwt credential credentials refresh".split(" ")
);
const CONSOLE_HOSTS = new Set(["app", "www", "api", "dev", "stage", "preprod", "dev3", "stage3", "dev-api", "stage-api", "v3-api", "dev3-api", "stage3-api"].map((sub) => `${sub}.refabric.com`));
const CONSOLE_MAX_ARGS = 8;
const CONSOLE_MAX_MESSAGE_CHARS = 256;
// Classification stops once this much output has been produced: the message is cut at 256 anyway.
const CONSOLE_MAX_CLASSIFIED_CHARS = CONSOLE_MAX_MESSAGE_CHARS * 4;
const CONSOLE_MAX_SCANNED_CHARS = 1000;
const CONSOLE_MAX_TOKEN_CHARS = 256;
const CONSOLE_MAX_TAG_CHARS = 64;
const FORMAT_SPECIFIER = /^%[sdifoOc]$/;
// A token made only of Unicode punctuation / symbols (`=`, `:`, `/`, `-`, `(`, `—`, `…`, `−` U+2212, `／`
// U+FF0F, …): dropped, so it neither consumes a pending redaction nor breaks a numeric run.
const PUNCTUATION_ONLY = /^[\p{P}\p{S}]+$/u;
const SMALL_INT = /^(?:0|[1-9]\d{0,2})$/;
// Digits with number punctuation: `+90`, `123-4567`, `(532)`, `10:30`, `2026-10-09`, `3.5`, `1,000`.
const NUMBER_LIKE = /^[+-]?\(?\d[\d.,:/()-]*$/;
const REDACTED_MARKER = "[redacted]";
const UNKNOWN_TAG = "<tag>";
const TEXT_BUCKETS = ["1k", "10k", "100k", "1m"] as const;
const textMarker = (length: number) => `[text:${TEXT_BUCKETS[Math.min(TEXT_BUCKETS.length - 1, Math.max(0, Math.floor(Math.log10(length)) - 3))]}+]`;

// Route templates from PATHS: a static segment must match exactly; `:param` / `:param?` match one segment.
const ROUTE_TEMPLATES = Object.values(PATHS).map((template) => template.split("/").filter(Boolean));

/** A path → the best-matching PATHS template, trimmed to the segments present, or null. */
const routeTemplateOf = (path: string): string | null => {
  const segments = path.split("/").filter(Boolean);
  let best: { statics: number; out: string } | null = null;
  ROUTE_TEMPLATES.forEach((template) => {
    if (segments.length > template.length) return;
    if (template.slice(segments.length).some((segment) => !segment.endsWith("?"))) return;
    let statics = 0;
    for (let index = 0; index < segments.length; index += 1) {
      const part = template[index];
      if (part.startsWith(":")) continue;
      if (part !== segments[index]) return;
      statics += 1;
    }
    if (!best || statics > best.statics) {
      best = {
        statics,
        out: `/${template
          .slice(0, segments.length)
          .map((part) => part.replace(/\?$/, ""))
          .join("/")}`,
      };
    }
  });

  return (best as { out: string } | null)?.out ?? null;
};

/** A URL / path token → `<origin|<host>><template|<path>>`, query and fragment dropped. Null if not URL-shaped. */
const classifyUrl = (core: string): string | null => {
  const absolute = /^([a-z][a-z0-9+.-]*:)?\/\/([^/?#]*)(.*)$/i.exec(core) ?? /^()([\w-]+(?:\.[\w-]+)+)(\/.*)$/.exec(core);
  if (!absolute && !core.startsWith("/")) return null;
  const [scheme, authority, rest] = absolute ? [absolute[1] ?? "", absolute[2], absolute[3]] : ["", "", core];
  const host = authority.toLowerCase();
  const origin = absolute ? (CONSOLE_HOSTS.has(host) && /^(?:https?:)?$/i.test(scheme) ? `${scheme.toLowerCase()}//${host}` : "<host>") : "";
  const path = rest.split(/[?#]/)[0];
  if (path === "" || path === "/") return origin + path;

  return origin + (routeTemplateOf(path) ?? "<path>");
};

/** Every item remembers its ARGUMENT index: the tag slot and the redaction span are per argument. */
type ConsoleItem = { arg: number } & ({ raw: string } | { marker: string });

/** Leading `[…]` of a string (whitespace, then at most 64 chars, then whitespace / `:` / end): the listed tag or `<tag>`. */
const leadingTag = (text: string): { tag: string; rest: string } | null => {
  let start = 0;
  while (start < text.length && start < CONSOLE_MAX_TAG_CHARS && /\s/.test(text[start])) start += 1;
  if (text[start] !== "[") return null;
  const close = text.slice(start, start + CONSOLE_MAX_TAG_CHARS + 2).indexOf("]");
  const after = text[start + close + 1];
  if (close < 2 || (after !== undefined && after !== ":" && !/\s/.test(after))) return null;
  const candidate = text.slice(start, start + close + 1);
  if (candidate.slice(1, -1).includes("[")) return null;

  // A `:` right after the tag is the boundary, not part of the next word (`[Tag]:password x`).
  const rest = text.slice(start + close + 1);

  return { tag: CONSOLE_TAGS.has(candidate) ? candidate : UNKNOWN_TAG, rest: rest.startsWith(":") ? rest.slice(1) : rest };
};

/** Raw text → items: `[json]` / `[text:<bucket>]` whole, else one raw item per whitespace-separated token. */
const textItems = (text: string, arg: number): ConsoleItem[] => {
  if (text.length > CONSOLE_MAX_SCANNED_CHARS) return [{ arg, marker: textMarker(text.length) }];
  // NFKC (after the length check): fullwidth `：` `＝`, fullwidth letters and digits become ASCII before any
  // classification, so they match the keyword, separator and number rules like their ASCII forms.
  const trimmed = text.normalize("NFKC").trim();
  if (/^[[{]/.test(trimmed)) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === "object") return [{ arg, marker: "[json]" }];
    } catch {
      // not JSON
    }
  }

  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .map((raw) => ({ arg, raw }));
};

/** Argument 0's text: its leading tag (listed or `<tag>`), then the rest as raw text. */
const firstArgItems = (text: string): ConsoleItem[] => {
  const whole = textItems(text, 0);
  if (whole.length === 1 && "marker" in whole[0]) return whole; // `[json]` / `[text:<bucket>]` (length checked first)
  const tagged = leadingTag(text.normalize("NFKC"));

  return tagged ? [{ arg: 0, marker: tagged.tag }, ...textItems(tagged.rest, 0)] : textItems(text, 0);
};

const argItems = (value: unknown, arg: number): ConsoleItem[] => {
  const kind = typeof value;
  if (kind === "string") return arg === 0 ? firstArgItems(value as string) : textItems(value as string, arg);
  if (value === null || value === undefined || kind === "boolean") return [{ arg, marker: String(value) }];
  if (kind === "number") return [{ arg, marker: Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 999 ? String(value) : "<num>" }];
  if (kind === "bigint") return [{ arg, marker: "<num>" }];
  if (kind === "function") return [{ arg, marker: "[function]" }];
  if (kind === "symbol") return [{ arg, marker: "[symbol]" }];

  return [{ arg, marker: Array.isArray(value) ? "[array]" : "[object]" }];
};

const WRAP = /^[("'`[{:=]*(.*?)[)"'`\]}:,;.!?]*$/;
const PAIR = /^([A-Za-z_][\w.-]{0,31})([:=])(.*)$/;

/** A value in a vocabulary `key=value` pair: a vocabulary word, a small integer, `<num>` or `<str>`. */
const pairValue = (value: string): string => {
  const lower = value.toLowerCase();
  if (CONSOLE_VOCABULARY.has(lower)) return lower;
  if (SMALL_INT.test(value)) return value;

  return NUMBER_LIKE.test(value) ? "<num>" : "<str>";
};

/** One raw token → output tokens (none to drop it). Never trusts the token for resembling a marker. */
const classifyRaw = (raw: string): string[] => {
  if (raw.length > CONSOLE_MAX_TOKEN_CHARS) return ["<str>"];
  if (PUNCTUATION_ONLY.test(raw) || FORMAT_SPECIFIER.test(raw)) return [];
  const url = classifyUrl(raw.replace(/[)"'`,;!?]+$/, ""));
  if (url !== null) return [url];
  const core = WRAP.exec(raw)?.[1] ?? raw;
  if (core === "") return [];
  const keyword = canonicalKeyword(core);
  if (keyword) return [keyword];
  // `<keyword>=value` / `<keyword>:value` with ANY key the keyword matcher accepts (digits first, no length cap
  // beyond the token cap): the keyword, then its value redacted; `token=` → the keyword alone (it arms).
  const separator = core.search(/[:=]/);
  const keyedKeyword = separator > 0 ? canonicalKeyword(core.slice(0, separator)) : null;
  if (keyedKeyword) return separator === core.length - 1 ? [keyedKeyword] : [keyedKeyword, REDACTED_MARKER];
  const lower = core.toLowerCase();
  if (CONSOLE_VOCABULARY.has(lower)) return [lower];
  if (SMALL_INT.test(core)) return [core];
  const pair = PAIR.exec(core);
  if (pair) {
    const key = pair[1].toLowerCase();
    if (CONSOLE_VOCABULARY.has(key)) return [pair[3] === "" ? key : `${key}${pair[2]}${pairValue(pair[3])}`];
  }

  return [NUMBER_LIKE.test(core) ? "<num>" : "<str>"];
};

const VOCABULARY_PAIR = /^([a-z_][\w.-]{0,31})([:=])(.+)$/;
const isBareNumeric = (token: string) => token === "<num>" || SMALL_INT.test(token);

/** The numeric value a token carries — a bare number, or a vocabulary pair's value — or null. */
const numericValueOf = (token: string): string | null => {
  if (isBareNumeric(token)) return token;
  const pair = VOCABULARY_PAIR.exec(token);

  return pair && CONSOLE_VOCABULARY.has(pair[1]) && isBareNumeric(pair[3]) ? pair[3] : null;
};

/** Numeric runs and the length bound over the classified token stream. */
const finish = (tokens: string[]): string => {
  // A run of 2+ numeric values: every value becomes `<num>`, then adjacent bare `<num>`s merge into one.
  const numbered = tokens.map((token, index) => {
    const inRun = numericValueOf(token) !== null && [tokens[index - 1], tokens[index + 1]].some((next) => next !== undefined && numericValueOf(next) !== null);
    if (!inRun) return token;

    return isBareNumeric(token) ? "<num>" : token.replace(/[:=].*$/, (separator) => `${separator[0]}<num>`);
  });
  const out = numbered.filter((token, index) => !(token === "<num>" && numbered[index - 1] === "<num>"));
  const kept: string[] = [];
  let length = 0;
  for (const token of out) {
    const next = length + (kept.length ? 1 : 0) + token.length;
    if (next > CONSOLE_MAX_MESSAGE_CHARS - 2) {
      kept.push("…");
      break;
    }
    kept.push(token);
    length = next;
  }

  return kept.join(" ");
};

/** Items → message: classify raw tokens, redact (fail closed) after a credential keyword, then `finish`. */
const buildMessage = (items: ConsoleItem[]): string => {
  const tokens: string[] = [];
  // After a keyword in argument `arg`: the rest of `arg` is redacted, and — while `next` — so is the whole
  // next argument that yields an item. Empty / dropped arguments and tokens therefore never absorb it.
  let pending: { arg: number; next: boolean } | null = null;
  let produced = 0;
  for (const item of items) {
    if (produced > CONSOLE_MAX_CLASSIFIED_CHARS) {
      tokens.push("…");
      break;
    }
    const classified = "marker" in item ? [item.marker] : classifyRaw(item.raw);
    if (classified.length === 0) continue;
    if (pending && item.arg !== pending.arg) pending = pending.next ? { arg: item.arg, next: false } : null;
    classified.forEach((token) => {
      const redact = pending !== null;
      if (!redact) tokens.push(token);
      else if (tokens[tokens.length - 1] !== REDACTED_MARKER) tokens.push(REDACTED_MARKER);
      produced += token.length + 1;
      if (CANONICAL_KEYWORDS.has(token) && !("marker" in item)) pending = { arg: item.arg, next: true };
    });
  }

  return finish(tokens);
};

// The beforeSend second pass must keep what the first pass (beforeBreadcrumb) built and rebuild everything
// else. Object identity does not survive: the SDK's normalizeEvent copies every breadcrumb with a spread
// before beforeSend (so a WeakSet of returned objects never matches there). An own ENUMERABLE property under a
// module-private Symbol does survive a spread, is never serialised (JSON ignores symbol keys) and is not
// forged by accident (only code that deliberately copies it off a real crumb could). Its value is the message it was built for, so a copy whose message was changed
// afterwards is rebuilt too. An unmarked console breadcrumb with no `arguments` — hand-added, or set on an
// event directly — is ALWAYS rebuilt from scratch, never trusted for looking minimised.
const MINIMISED = Symbol("minimisedConsoleMessage");
type MarkedBreadcrumb = Breadcrumb & { [MINIMISED]?: string };

/** A fresh console breadcrumb: type, category, level, timestamp, the vocabulary message, `data.logger` only. Idempotent. */
export const minimiseConsoleBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const level = breadcrumb.level;
  try {
    const original = typeof breadcrumb.message === "string" ? breadcrumb.message : "";
    const rawArgs: unknown = breadcrumb.data?.arguments;
    let message: string;
    if (Array.isArray(rawArgs)) {
      // Indexed reads only — no method of the container (slice/map) is trusted.
      const count = rawArgs.length;
      const items: ConsoleItem[] = original.startsWith("Assertion failed:")
        ? [
            { arg: -1, marker: "assertion" },
            { arg: -1, marker: "failed" },
          ]
        : [];
      for (let index = 0; index < Math.min(count, CONSOLE_MAX_ARGS); index += 1) items.push(...argItems(rawArgs[index], index));
      if (count > CONSOLE_MAX_ARGS) items.push({ arg: CONSOLE_MAX_ARGS, marker: "[more]" });
      message = buildMessage(items);
    } else {
      // No arguments: the beforeSend second pass over a breadcrumb the first pass marked (kept as is), or
      // anything else — a hand-added console breadcrumb — rebuilt from scratch like a raw argument 0.
      message = (breadcrumb as MarkedBreadcrumb)[MINIMISED] === original ? original : buildMessage(firstArgItems(original));
    }

    const out: MarkedBreadcrumb = { type: breadcrumb.type, category: "console", level, timestamp: breadcrumb.timestamp, message, data: { logger: "console" } };
    out[MINIMISED] = message;

    return out;
  } catch {
    const unavailable: MarkedBreadcrumb = { category: "console", level, timestamp: breadcrumb.timestamp, message: "[unavailable]", data: { logger: "console" } };
    unavailable[MINIMISED] = "[unavailable]";

    return unavailable;
  }
};

// ── hooks ────────────────────────────────────────────────────────────────────────────────────────

// Resumable-upload session ids (utils/sentry/scrubUploadSession) are scrubbed FIRST inside each hook below, so
// `instrument.ts` passes the hooks by identity and there stays one scrub owner. A console breadcrumb skips that
// walk: the minimiser never reads an argument's properties and rebuilds the message from its vocabulary, which
// drops every query value, `upload_id` included.

/** `beforeBreadcrumb`: runs before Replay receives the breadcrumb, so Replay's copy is clean too. */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (breadcrumb.category === "console") return minimiseConsoleBreadcrumb(breadcrumb);
  const out: Breadcrumb = { ...scrubUploadSession(breadcrumb) };
  if (typeof out.message === "string") out.message = sanitizeUrlsInText(out.message);
  if (out.data) out.data = sanitizeDeep(out.data);

  return out;
};

/** `beforeSendSpan`: covers transaction child spans AND standalone spans (INP's `transaction` attribute). */
export const scrubSpan = (input: SpanJSON): SpanJSON => {
  const span = scrubUploadSession(input);
  if (typeof span.description === "string") span.description = sanitizeUrlsInText(span.description);
  if (span.data) {
    const data: AnyRecord = sanitizeDeep({ ...span.data });
    delete data["http.fragment"];
    if (typeof span.data["http.query"] === "string") data["http.query"] = sanitizeUrlsInText(span.data["http.query"]);
    if (typeof span.data.transaction === "string") data.transaction = sanitizeRouteOrLabel(span.data.transaction);
    span.data = data;
  }

  return span;
};

const scrubRequest = (request: AnyRecord) => {
  if (typeof request.url === "string") request.url = sanitizeUrl(request.url);
  if (typeof request.query_string === "string") request.query_string = sanitizeQuery(request.query_string.replace(/^\?/, ""));
  else if (request.query_string !== undefined) request.query_string = sanitizeDeep(request.query_string);
  if (request.headers && typeof request.headers === "object") {
    // Any header name, any case: the referrer is a URL, every other value is text that may embed one.
    request.headers = Object.fromEntries(
      Object.entries(request.headers as AnyRecord).map(([name, value]) => [name, typeof value !== "string" ? value : /^referr?er$/i.test(name) ? sanitizeUrl(value) : sanitizeUrlsInText(value)])
    );
  }
  if (request.data !== undefined) request.data = sanitizeDeep(request.data);
  if (request.cookies !== undefined) request.cookies = sanitizeDeep(request.cookies);
};

// Stack frame file names are bundle URLs, or the page URL for inline / eval code. Only a NETWORK URL is
// sanitised; virtual source ids (`webpack:///./src/…`, `app:///…`), `<anonymous>` and `native` are kept
// byte for byte, so symbolication and source-map lookup see exactly what they saw before.
export const scrubFrameFilename = (filename: string): string => {
  // A network URL — `https:////x/…` included, which the browser reads as `https://x/…` — or a
  // protocol-relative `//host/…`; `///` without a scheme is a virtual id, not a host.
  if (/^(?:https?|wss?):\/\//i.test(filename) || /^\/\/(?!\/)/.test(filename)) return sanitizeUrl(filename);

  return /^(?:data|blob):/i.test(filename) ? REDACTED : filename;
};

const scrubStacktrace = (stacktrace: AnyRecord | undefined) => {
  if (!Array.isArray(stacktrace?.frames)) return;
  stacktrace?.frames.forEach((frame: AnyRecord) => {
    if (typeof frame.filename === "string") frame.filename = scrubFrameFilename(frame.filename);
    if (typeof frame.abs_path === "string") frame.abs_path = scrubFrameFilename(frame.abs_path);
  });
};

// R-1 (2026-10-31 main sync): main's credential-named keys (`SECRET_FIELD_NAMES`: token, link_token, password, …) hold
// "[Filtered]" in an event's request / extra / tags / contexts, as main's `scrubSentryEvent` did (user decision D3: our
// hooks own the event, main's safeguard is kept inside them). KEYS only, the same rule and depth as main's walk
// (utils/sentry/scrubUploadSession): every string is this policy's job, so URLs keep this policy's `[redacted]`.
const filterSecretFields = (value: unknown, depth = 0): unknown => {
  if (!value || typeof value !== "object" || depth > 6) return value;
  if (Array.isArray(value)) return value.map((item) => filterSecretFields(item, depth + 1));

  return Object.fromEntries(
    Object.entries(value as AnyRecord).map(([key, item]) => [key, typeof item === "string" && SECRET_FIELD_NAMES.has(key) ? "[Filtered]" : filterSecretFields(item, depth + 1)])
  );
};

/**
 * The final scrub for errors, messages, transactions and replay events. Runs LAST in `beforeSend`, so
 * nothing an earlier processor added escapes. Mutates and returns the event, as SDK hooks do.
 */
export const scrubEvent = <T extends Event | TransactionEvent>(event: T): T => {
  const e = event as AnyRecord;
  if (e.request) {
    scrubRequest(e.request);
    e.request = filterSecretFields(e.request);
  }
  if (typeof e.transaction === "string") e.transaction = sanitizeRouteOrLabel(e.transaction);
  if (e.tags) e.tags = filterSecretFields(sanitizeDeep(e.tags));
  if (e.extra) e.extra = filterSecretFields(sanitizeDeep(e.extra));
  if (e.contexts) {
    // `contexts.state` is the Redux projection's (store/sentryRedux), already an allowlist: left as it is.
    e.contexts = Object.fromEntries(Object.entries(e.contexts as AnyRecord).map(([key, value]) => [key, key === "state" ? value : filterSecretFields(sanitizeDeep(value))]));
    if (e.contexts.trace?.data) delete e.contexts.trace.data["http.fragment"];
  }
  if (Array.isArray(e.breadcrumbs)) e.breadcrumbs = e.breadcrumbs.map(scrubBreadcrumb);
  if (Array.isArray(e.fingerprint)) e.fingerprint = e.fingerprint.map((entry: unknown) => (typeof entry === "string" ? sanitizeRouteOrLabel(entry) : entry));
  if (Array.isArray(e.exception?.values)) {
    e.exception.values.forEach((value: AnyRecord) => {
      if (typeof value.value === "string") value.value = sanitizeUrlsInText(value.value);
      scrubStacktrace(value.stacktrace);
    });
  }
  if (typeof e.message === "string") e.message = sanitizeUrlsInText(e.message);
  if (e.logentry) {
    if (typeof e.logentry.message === "string") e.logentry.message = sanitizeUrlsInText(e.logentry.message);
    if (Array.isArray(e.logentry.params)) e.logentry.params = sanitizeDeep(e.logentry.params);
  }
  if (Array.isArray(e.spans)) e.spans = e.spans.map(scrubSpan);
  // The SDK copies a debug-ID frame's file name into debug_meta: same transform, so the pair still matches.
  if (Array.isArray(e.debug_meta?.images)) {
    e.debug_meta.images.forEach((image: AnyRecord) => {
      if (typeof image.code_file === "string") image.code_file = scrubFrameFilename(image.code_file);
    });
  }
  if (e.measurements && typeof e.measurements === "object") {
    e.measurements = Object.fromEntries(Object.entries(e.measurements as AnyRecord).map(([name, value]) => [sanitizeUrlsInText(name), value]));
  }
  const dsc = e.sdkProcessingMetadata?.dynamicSamplingContext;
  if (dsc && typeof dsc.transaction === "string") dsc.transaction = sanitizeRouteOrLabel(dsc.transaction);

  return event;
};

/** `beforeSendTransaction`: the pageload/navigation name is the raw pathname (no router-v6 wrapper). */
export const scrubTransactionEvent = (event: TransactionEvent): TransactionEvent => scrubEvent(event);

/** `client.on("createDsc")`: the dynamic sampling context becomes the envelope header `trace.transaction`. */
const scrubDsc = (dsc: { transaction?: string }): void => {
  if (typeof dsc.transaction === "string") dsc.transaction = sanitizeRouteOrLabel(dsc.transaction);
};

/**
 * `client.on("beforeEnvelope")`: the last stop before the transport. An INHERITED (frozen) DSC — from
 * incoming `baggage` — is returned without emitting `createDsc`, and a standalone span's header is built
 * before `beforeSendSpan`, so the header itself is scrubbed here too.
 */
const scrubEnvelopeHeaders = (envelope: unknown): void => {
  const trace = (envelope as [AnyRecord?] | undefined)?.[0]?.trace;
  if (trace && typeof trace.transaction === "string") trace.transaction = sanitizeRouteOrLabel(trace.transaction);
};

/** Event processor for `replay_event` — `beforeSend` / `beforeSendTransaction` never run for those. */
export const scrubReplayEventProcessor = (event: ProcessedEvent): ProcessedEvent => {
  if ((event as AnyRecord).type !== "replay_event") return event;
  const scrubbed = scrubEvent(event as Event) as AnyRecord;
  if (Array.isArray(scrubbed.urls)) scrubbed.urls = scrubbed.urls.map((url: unknown) => (typeof url === "string" ? sanitizeUrl(url) : url));

  return scrubbed as ProcessedEvent;
};

const REPLAY_CUSTOM_EVENT = 5;

const isConsoleBreadcrumbEvent = (event: RecordingEvent): boolean => {
  const custom = event as unknown as { type?: number; data?: { tag?: string; payload?: { category?: unknown } | null } };

  return custom?.type === REPLAY_CUSTOM_EVENT && custom.data?.tag === "breadcrumb" && custom.data.payload?.category === "console";
};

/** Replay `beforeAddRecordingEvent`: custom recording events only (performance spans, breadcrumbs). */
export const scrubRecordingEvent = (input: RecordingEvent): RecordingEvent => {
  // A console breadcrumb skips the upload walk here too: the walk rebuilds the payload with `Object.entries`, which
  // drops the `MINIMISED` mark Replay's copy still carries, so the minimiser would rebuild its own output and turn
  // every marker into `<str>`. `scrubBreadcrumb` below routes it to the minimiser, which drops `upload_id` anyway.
  const event = isConsoleBreadcrumbEvent(input) ? input : scrubReplayRecordingEvent(input);
  const custom = event as unknown as { type?: number; data?: { tag?: string; payload?: unknown } };
  if (custom.type !== REPLAY_CUSTOM_EVENT || !custom.data) return event;
  const { tag, payload } = custom.data;
  custom.data = { ...custom.data, payload: tag === "breadcrumb" && payload && typeof payload === "object" ? scrubBreadcrumb(payload as Breadcrumb) : sanitizeDeep(payload) };

  return event;
};

const installedClients = new WeakSet<object>();
let replayProcessorInstalled = false;

/** Post-`init` registrations, idempotent per client. Called by `instrument.ts`; tests call it on their own client. */
export const installSentryPrivacy = (client: Client | undefined): void => {
  if (!client || installedClients.has(client)) return;
  installedClients.add(client);
  client.on("createDsc", scrubDsc);
  client.on("beforeEnvelope", scrubEnvelopeHeaders);
  if (!replayProcessorInstalled) {
    replayProcessorInstalled = true;
    Sentry.addEventProcessor(scrubReplayEventProcessor);
  }
};
